// Unpublished Work © 2020-2024 Deere & Company.
import {createAxiosClient} from 'Common/utils/axios-client';
import {CONFLICT, UNAUTHORIZED} from 'Common/constants/errors';
import {getStore} from 'Store/store';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {isDuplicateConflict} from 'OnLabor/workboard/utils/workboard-conflict-utils';
import {replaceLocation} from 'Utils/window-location-utils';

const {addToast} = useToast();

function toastIfForbidden() {
    const translations = getStore().getState().translations;

    addToast({
        message: translations.ONLINK_WRONG_ORG_ERROR,
        type: TOAST_TYPE.ERROR,
        persist: true
    });
}

function redirectIfUnauthorized(route) {
    if (typeof route === 'string') {
        replaceLocation(route);
    }
}

function interceptErrorResponse(error) {
    const response = error.response;

    switch (response?.status) {
        case UNAUTHORIZED:
            redirectIfUnauthorized(response.data);
            break;
        case CONFLICT:
            if (!isDuplicateConflict(error)) {
                toastIfForbidden();
            }
            break;
        default:
            break;
    }
    throw error;
}

function addInterceptor(axiosClient) {
    axiosClient.interceptors.response.use(null, interceptErrorResponse);
}

const METHODS_WITH_CONFIG_SECOND = ['delete', 'get', 'head', 'options'];
const METHODS_WITH_CONFIG_THIRD = ['patch', 'post', 'put'];

function attachOptionalSignal(axiosClient, signal) {
    const isValid =
        signal &&
        typeof signal.addEventListener === 'function' &&
        !signal.aborted;

    if (!isValid) {
        return axiosClient;
    }

    // The client is shared by every caller, so the signal has to belong to the calls made through this wrapper.
    const withSignal = (config) => config?.signal ? config : {
        ...config,
        signal
    };
    const scopedClient = Object.create(axiosClient);

    METHODS_WITH_CONFIG_SECOND.forEach((method) => {
        scopedClient[method] = (url, config) => axiosClient[method](url, withSignal(config));
    });
    METHODS_WITH_CONFIG_THIRD.forEach((method) => {
        scopedClient[method] = (url, data, config) => axiosClient[method](url, data, withSignal(config));
    });
    scopedClient.request = (config) => axiosClient.request(withSignal(config));

    return scopedClient;
}

function createUiAxiosClient(timeout, headers) {
    return createAxiosClient('ui', {
        timeout,
        headers
    }, addInterceptor);
}

function createUiAxiosClientWithSignal(signal, timeout, headers) {
    const client = createUiAxiosClient(timeout, headers);

    return attachOptionalSignal(client, signal);
}

export {
    createUiAxiosClient,
    interceptErrorResponse,
    attachOptionalSignal,
    createUiAxiosClientWithSignal
};
